import { DeleteOutlined, EditOutlined } from "@ant-design/icons"
import type { TableColumnsType, TablePaginationConfig, TableProps } from "antd"
import { Button, message, Modal, Space, Table } from "antd"
import { useCallback, useEffect } from "react"
import { useForm, useWatch } from "react-hook-form"
import { useNavigate } from "react-router"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { CategoryData, FormValuesCategory, Pagination, PostResponse } from "~/types/types"
import { deleteCategory, fetchCategories } from "../services/category"

interface CategoryFilterProps {
  searchTerm: string
  key: number
}

const CategoryList = ({ searchTerm, key }: CategoryFilterProps) => {
  const { control, setValue } = useForm<FormValuesCategory>({
    defaultValues: {
      pagination: {
        page: 1,
        pageSize: 5,
        current: 1,
        total: 0,
      },
    },
  })
  const dataItem = useWatch({ control, name: "dataItem" })
  const loading = useWatch({ control, name: "loading" })
  const pagination = useWatch({ control, name: "pagination" })
  const permissions = JSON.parse(localStorage.getItem("permissions") || "{}")
  const navigate = useNavigate()

  const fetchCategory = async (paginate?: Pagination, searchTerm?: string) => {
    const response = await fetchCategories(searchTerm)
    return response
  }

  const handleFetchData = useCallback(
    async (paginate?: typeof pagination) => {
      setValue("loading", true)
      try {
        const response = await fetchCategory(paginate, searchTerm)
        if (response?.error) {
          setValue("error", response?.error?.message)
          return
        }
        setValue("dataItem", response?.data?.length > 0 ? response.data : [])
        setValue("pagination", {
          ...pagination,
          total: response?.meta?.pagination.total ?? 0,
          current: response?.meta?.pagination.page ?? 1,
        })
        setValue("error", null)
      } catch (err) {
        setValue("error", err as string)
      } finally {
        setValue("loading", false)
      }
    },
    [setValue, searchTerm],
  )

  const handleEdit = (id: number) => {
    navigate(`${Paths.CATEGORIES.EDIT.PATH}/${id}`)
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this post?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setValue("loading", true)
          await deleteCategory(id)
          setValue(
            "dataItem",
            dataItem.filter(post => post.id !== id),
          )
          handleFetchData()
          setValue("loading", false)
        } catch {
          message.error(t("Handle Delete Error"))
        }
      },
    })
  }

  const onChange: TableProps<CategoryData>["onChange"] = (pagination: TablePaginationConfig) => {
    const newPagination: Pagination = {
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
      total: pagination.total || 0,
      page: pagination.current || 1,
    }
    setValue("pagination", newPagination)
    handleFetchData(newPagination)
  }

  useEffect(() => {
    handleFetchData()
  }, [handleFetchData, key])

  const columns: TableColumnsType<CategoryData> = [
    {
      title: t("Name"),
      dataIndex: ["attributes", "name"],
      width: "25%",
    },
    {
      title: t("Description"),
      dataIndex: ["attributes", "description"],
      width: "20%",
    },
    {
      title: t("Slug"),
      dataIndex: ["attributes", "slug"],
      width: "20%",
    },
    {
      title: t("Count"),
      dataIndex: ["attributes", "posts", "data"],
      width: "10%",
      render: (data: PostResponse[]) => data.length || 0,
    },
  ]
  if (permissions.cateogry.update.enabled || permissions.cateogry.delete.enabled) {
    columns.push({
      title: t("Action"),
      key: "action",
      width: "15%",
      render: (_, record) => (
        <Space size="middle" className="justify-center items-center">
          {permissions.cateogry.update.enabled && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              className="bg-blue-500 px-4 py-2 rounded"
              onClick={() => handleEdit(record.id)}
            >
              {t("Edit")}
            </Button>
          )}
          {permissions.cateogry.delete.enabled && (
            <Button danger type="primary" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
              {t("Delete")}
            </Button>
          )}
        </Space>
      ),
    })
  }

  return (
    <div>
      <Table
        className="mt-4 rounded-[6px] border border-gray-300 custom-table"
        rowKey="id"
        columns={columns}
        loading={loading}
        dataSource={dataItem || []}
        onChange={onChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        scroll={{ x: 1000, y: 500 }}
        rowClassName={(record, index) => (index % 2 === 0 ? "bg-[#f6f7f7]" : "bg-white")}
      />
    </div>
  )
}

export default CategoryList
